import React, { createContext, useContext, useState, useCallback, useEffect } from 'react'
import { parseJwt, isTokenExpired } from '../utils/jwt.js'
import authapi from '../services/auth'

const AuthContext = createContext(null)

const TOKEN_KEY = "access-token"

const ALLOWED_ROLES = ["SUPER_ADMIN", "AXISERP_HELPERS_USER"]

export const AuthProvider = ({ children }) => {
    const [state, setState] = useState(() => {
        const saved = localStorage.getItem(TOKEN_KEY)
        if (saved) {
            try {
                const user = parseJwt(saved)
                if (!isTokenExpired(user.exp)) {
                    // Rol kontrolü — kayıtlı token'da uygun rol yoksa giriş yapılmış sayma
                    const hasAllowedRole = user.roles?.some(r => ALLOWED_ROLES.includes(r))
                    if (hasAllowedRole) {
                        return { token: saved, user, isLogged: true }
                    }
                }
            } catch { }
        }
        localStorage.removeItem(TOKEN_KEY)
        return { token: null, user: null, isLogged: false }
    })

    const login = useCallback((token) => {
        const user = parseJwt(token)

        // Rol kontrolü
        const hasAllowedRole = user.roles?.some(r => ALLOWED_ROLES.includes(r))
        if (!hasAllowedRole) {
            // Yetkisiz kullanıcı — logout isteği at ve reddet
            authapi.post('/Auth/logout', {}).catch(() => { })
            localStorage.removeItem(TOKEN_KEY)
            setState({ token: null, user: null, isLogged: false })
            throw new Error("UNAUTHORIZED_ROLE")
        }

        localStorage.setItem(TOKEN_KEY, token)
        setState({ token, user, isLogged: true })
    }, [])

    const logout = useCallback(async () => {
        try { await authapi.post('/Auth/logout', {}) } catch { }
        localStorage.removeItem(TOKEN_KEY)
        setState({ token: null, user: null, isLogged: false })
    }, [])

    const hasRole = useCallback((role) => state.user?.roles?.includes(role) ?? false, [state.user])
    const hasPerm = useCallback((perm) => state.user?.permissions?.includes(perm) ?? false, [state.user])

    /* Token yenilendi (401 sonrası otomatik) */
    useEffect(() => {
        const onRefresh = (e) => {
            const token = e.detail.token
            try {
                const user = parseJwt(token)
                if (!isTokenExpired(user.exp)) {
                    const hasAllowedRole = user.roles?.some(r => ALLOWED_ROLES.includes(r))
                    if (hasAllowedRole) {
                        localStorage.setItem(TOKEN_KEY, token)
                        setState({ token, user, isLogged: true })
                        return
                    }
                }
            } catch { }
            // Token geçersiz veya rol yetersiz
            logout()
        }
        const onFailed = () => logout()
        window.addEventListener('token-refreshed', onRefresh)
        window.addEventListener('auth-failed', onFailed)
        return () => {
            window.removeEventListener('token-refreshed', onRefresh)
            window.removeEventListener('auth-failed', onFailed)
        }
    }, [logout])

    /* Token süresi dolduysa otomatik çıkış */
    useEffect(() => {
        if (!state.user) return
        const msLeft = state.user.exp * 1000 - Date.now()
        if (msLeft <= 0) { logout(); return }
        const timer = setTimeout(logout, msLeft)
        return () => clearTimeout(timer)
    }, [state.user, logout])

    return (
        <AuthContext.Provider value={{ ...state, login, logout, hasRole, hasPerm }}>
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = () => {
    const ctx = useContext(AuthContext)
    if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
    return ctx
}